import { FileText } from "lucide-react"

import { getNodeBody } from "../diagramEditorModel"
import { resolvePrefs } from "../types"
import type { DiagramNode, DiagramSpec } from "../types"

interface Props {
  spec: DiagramSpec | null
}

interface PreviewNode {
  id: string
  label: string
  isGroup: boolean
  backgroundColor?: string
  color: string
  x: number
  y: number
  width: number
  height: number
}

const FALLBACK_COLOR = "#8b5cf6"
const PREVIEW_WIDTH = 560
const PREVIEW_HEIGHT = 260
const PREVIEW_PADDING = 28

function getNodeColor(node: DiagramNode, diagram: DiagramSpec) {
  return diagram.layers.find((layer) => layer.id === node.layer)?.color ?? FALLBACK_COLOR
}

function getAbsolutePosition(node: DiagramNode, diagram: DiagramSpec) {
  const position = node.position ?? { x: 0, y: 0 }
  if (!node.parentNode) return position

  const parent = diagram.nodes.find((candidate) => candidate.id === node.parentNode)
  const parentPosition = parent?.position ?? { x: 0, y: 0 }
  return {
    x: parentPosition.x + position.x,
    y: parentPosition.y + position.y,
  }
}

function getPreviewNodes(diagram: DiagramSpec) {
  const prefs = resolvePrefs(diagram.preferences)
  return diagram.nodes.map<PreviewNode>((node) => {
    const position = getAbsolutePosition(node, diagram)
    return {
      id: node.id,
      label: getNodeBody(node),
      isGroup: Boolean(node.isGroup),
      backgroundColor: node.backgroundColor,
      color: getNodeColor(node, diagram),
      x: position.x,
      y: position.y,
      width: node.width ?? prefs.nodeWidth,
      height: node.height ?? (node.isGroup ? 260 : 90),
    }
  })
}

function getBounds(nodes: PreviewNode[]) {
  if (nodes.length === 0) return { minX: 0, minY: 0, width: 1, height: 1 }

  const values = nodes.reduce(
    (acc, node) => ({
      minX: Math.min(acc.minX, node.x),
      minY: Math.min(acc.minY, node.y),
      maxX: Math.max(acc.maxX, node.x + node.width),
      maxY: Math.max(acc.maxY, node.y + node.height),
    }),
    { minX: Number.POSITIVE_INFINITY, minY: Number.POSITIVE_INFINITY, maxX: 0, maxY: 0 },
  )

  return {
    minX: values.minX,
    minY: values.minY,
    width: Math.max(values.maxX - values.minX, 1),
    height: Math.max(values.maxY - values.minY, 1),
  }
}

function truncateLabel(label: string) {
  const firstLine = label.split("\n").find((line) => line.trim().length > 0) ?? "Untitled"
  return firstLine.length > 28 ? `${firstLine.slice(0, 25)}...` : firstLine
}

export function DiagramPreviewCard({ spec }: Props) {
  if (!spec) {
    return (
      <div className="flex h-full items-center justify-center bg-zinc-950 text-zinc-600">
        <FileText className="h-8 w-8" />
      </div>
    )
  }

  const previewNodes = getPreviewNodes(spec)
  const bounds = getBounds(previewNodes)
  const scale = Math.min(
    (PREVIEW_WIDTH - PREVIEW_PADDING * 2) / bounds.width,
    (PREVIEW_HEIGHT - PREVIEW_PADDING * 2) / bounds.height,
  )
  const offsetX = PREVIEW_PADDING - bounds.minX * scale + (PREVIEW_WIDTH - bounds.width * scale - PREVIEW_PADDING * 2) / 2
  const offsetY = PREVIEW_PADDING - bounds.minY * scale + (PREVIEW_HEIGHT - bounds.height * scale - PREVIEW_PADDING * 2) / 2
  const nodesById = new Map(previewNodes.map((node) => [node.id, node]))

  return (
    <div className="relative h-full overflow-hidden bg-white">
      <div className="absolute inset-0 opacity-45 [background-image:radial-gradient(#a3a3a3_1px,transparent_1px)] [background-size:14px_14px]" />
      <svg className="absolute inset-0 h-full w-full" viewBox={`0 0 ${PREVIEW_WIDTH} ${PREVIEW_HEIGHT}`}>
        {spec.edges.map((edge) => {
          const source = nodesById.get(edge.source)
          const target = nodesById.get(edge.target)
          if (!source || !target) return null

          const x1 = offsetX + (source.x + source.width / 2) * scale
          const y1 = offsetY + (source.y + source.height / 2) * scale
          const x2 = offsetX + (target.x + target.width / 2) * scale
          const y2 = offsetY + (target.y + target.height / 2) * scale

          return (
            <line
              key={edge.id}
              x1={x1}
              y1={y1}
              x2={x2}
              y2={y2}
              stroke="#64748b"
              strokeWidth={1.5}
              strokeDasharray={edge.style === "dashed" ? "5 4" : undefined}
            />
          )
        })}
      </svg>
      {previewNodes
        .sort((left, right) => Number(right.isGroup) - Number(left.isGroup))
        .map((node) => (
          <div
            key={node.id}
            className={`absolute overflow-hidden border text-[10px] leading-tight ${
              node.isGroup ? "rounded-md border-dashed" : "rounded bg-white shadow-sm"
            }`}
            style={{
              left: offsetX + node.x * scale,
              top: offsetY + node.y * scale,
              width: Math.max(node.width * scale, node.isGroup ? 48 : 28),
              height: Math.max(node.height * scale, node.isGroup ? 36 : 20),
              borderColor: node.color,
              backgroundColor: node.backgroundColor ?? (node.isGroup ? `${node.color}10` : "#ffffff"),
              color: "#27272a",
            }}
          >
            {!node.isGroup && <div className="truncate px-1 py-0.5 font-medium">{truncateLabel(node.label)}</div>}
          </div>
        ))}
    </div>
  )
}
